import { pendo } from '@frontend/tracking';
import { getSendFormOrigin, getSendFormStep, getWindowDimensions } from './send-form-context-details.utils';
import { getTimeTakenToComplete } from './time.utils';
import { PRESET_STORAGE_VALUES, TRACKER_STORAGE_KEYS, PENDO_TRACKING_KEYS } from './tracker.constants';
import { SendFormPendoTrackerPayload, SendFormTrackingPayload, MyFormsVisitPendoTrackerPayload } from './tracker.type';

/**
 * Clears the session storage for the send form tracker and My Forms page tracker.
 */
function clearStorage() {
  // Loop through the keys and remove them from session storage
  Object.values(TRACKER_STORAGE_KEYS).forEach((key) => {
    window.sessionStorage.removeItem(key);
  });
}

/**
 * Starts the tracker for the send form process.
 * This function should be called when the user initiates the process of sending a form.
 * It sets a timestamp in session storage to mark the start of the process.
 */
export function startSendFormTracker() {
  window.sessionStorage.setItem(TRACKER_STORAGE_KEYS.sendFormInitiatedAt, Date.now().toString());
}

/**
 * Marks that the user has completed the send form process, so that we can ignore the initial timestamp.
 * This is used to prevent tracking the send form process if the user navigates away after completing it.
 */
function markSendFormAsTracked() {
  window.sessionStorage.setItem(
    TRACKER_STORAGE_KEYS.sendFormInitiatedAt,
    PRESET_STORAGE_VALUES.sendForm.trackingComplete
  );
}

/**
 * Checks if the send form process was tracked.
 * This function checks if the session storage has a specific value indicating that the send form process was initiated.
 */
function wasSendFormTracked(): boolean {
  const storageValue = window.sessionStorage.getItem(TRACKER_STORAGE_KEYS.sendFormInitiatedAt);
  return storageValue === PRESET_STORAGE_VALUES.sendForm.trackingComplete;
}

/**
 * Resets the send form tracker by removing the timestamp from session storage.
 * This function should be called when the user navigates away from the send form process.
 */
export function resetSendFormTracker() {
  // Skip resetting if the send form process was tracked.
  if (wasSendFormTracked()) {
    return;
  }

  window.sessionStorage.removeItem(TRACKER_STORAGE_KEYS.sendFormInitiatedAt);
}

/**
 * @returns The timestamp when the send form process was initiated, or null if it was not initiated.
 */
export function getSendFormInitiatedAt() {
  const storageValue = window.sessionStorage.getItem(TRACKER_STORAGE_KEYS.sendFormInitiatedAt);
  if (!storageValue || storageValue === PRESET_STORAGE_VALUES.sendForm.trackingComplete) {
    return null;
  }

  try {
    const parsedValue = parseInt(storageValue, 10);
    if (isNaN(parsedValue)) {
      return null;
    }
    return parsedValue;
  } catch (error) {
    return null;
  }
}

/**
 * Tracks the completion or abortion of the send form process.
 * This function should be called when the user completes or aborts the send form process.
 */
export function finishSendFormTracking(payload: SendFormTrackingPayload) {
  const initiatedAt = getSendFormInitiatedAt();
  if (!initiatedAt) {
    return;
  }

  const origin = getSendFormOrigin(payload.origin);
  const { width, height } = getWindowDimensions();
  if (payload.isComplete) {
    const trackerPayload: SendFormPendoTrackerPayload = {
      browserHeight: height.toString(),
      browserWidth: width.toString(),
      origin,
      flowStatus: 'complete',
      deliveryOption: payload.deliveryOption,
      timeTakenToComplete: getTimeTakenToComplete(initiatedAt, Date.now()),
    };
    pendo.track(PENDO_TRACKING_KEYS.sendForm, trackerPayload);
    markSendFormAsTracked();
    return;
  }

  const step = getSendFormStep(payload.step);
  const trackerPayload: SendFormPendoTrackerPayload = {
    browserHeight: height.toString(),
    browserWidth: width.toString(),
    origin,
    flowStatus: 'abort',
    step,
    timeSpentOnPage: getTimeTakenToComplete(initiatedAt, Date.now()),
  };
  pendo.track(PENDO_TRACKING_KEYS.sendForm, trackerPayload);
  markSendFormAsTracked();
}

/**
 * Starts the tracker for the My Forms page.
 * This function should be called when the user visits the My Forms page.
 * It sets a timestamp in session storage to mark the visit.
 */
export function startMyFormsTracker() {
  window.sessionStorage.setItem(TRACKER_STORAGE_KEYS.myFormsVisitedAt, Date.now().toString());
}

/**
 * @returns The timestamp when the My Forms page was visited, or null if it was not visited.
 */
function getMyFormsVisitedAt(): number | null {
  const storageValue = window.sessionStorage.getItem(TRACKER_STORAGE_KEYS.myFormsVisitedAt);
  if (!storageValue) {
    return null;
  }

  try {
    const parsedValue = parseInt(storageValue, 10);
    if (isNaN(parsedValue)) {
      return null;
    }
    return parsedValue;
  } catch (error) {
    return null;
  }
}

export function trackIfNoFormsWereSent() {
  // Track the visit to the My Forms page only if the send form process was not tracked.
  if (!wasSendFormTracked()) {
    const { width, height } = getWindowDimensions();
    const trackerPayload: MyFormsVisitPendoTrackerPayload = {
      browserHeight: height.toString(),
      browserWidth: width.toString(),
      flowStatus: 'read-only',
      timeSpentOnPage: '0s', // Default value, will be updated if the user stays on the page
    };
    const visitedAt = getMyFormsVisitedAt();
    if (visitedAt) {
      trackerPayload.timeSpentOnPage = getTimeTakenToComplete(visitedAt, Date.now());
    }
    pendo.track(PENDO_TRACKING_KEYS.myForms, trackerPayload);
  }
  clearStorage();
}
